import { fetchAuthSession, signIn, signOut } from 'aws-amplify/auth'
import { useLocation, useNavigate } from 'react-router'
import { Paths } from 'src/constants/paths'
import { login } from 'src/legacy_modules/auth/login/request'
import { routes } from 'src/legacy_modules/routes'
import { useRequestState } from 'src/legacy_modules/server/use-request-state'

import { usePostLoginNavigation } from './usePostLoginNavigation'

export type OnSubmit = ({
  email,
  password,
}: {
  email: string
  password: string
}) => Promise<void>

export const useLogin = ({ onlyPassword = false } = {}) => {
  const {
    requestDone,
    requestFailed,
    requestStarted,
    requesting,
    errorMessage,
  } = useRequestState()
  const { jumpAfterLogin } = usePostLoginNavigation()

  const navigate = useNavigate()

  const location = useLocation()

  const onSubmit: OnSubmit = async ({ email, password }) => {
    try {
      requestStarted()
      // 別ユーザーでログインしている場合、一度ログアウトする
      await signOut()
      const {
        nextStep: { signInStep },
      } = await signIn({
        username: email,
        password,
      })

      // 本パスワード登録済みでない場合
      if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        navigate(Paths.RegisterPassword)
        return
      }

      // MFAを設定している場合
      if (signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        navigate(routes.twoFactor, { state: location.state })
        return
      }

      if (signInStep !== 'DONE') {
        requestFailed('ログインに失敗しました。')
        return
      }

      const currentSession = await fetchAuthSession()
      const idToken = currentSession.tokens?.idToken?.toString()
      if (!idToken) {
        requestFailed('ログインに失敗しました。')
        return
      }

      await login(idToken, false)
      await jumpAfterLogin()
    } catch (error) {
      if (error.message === 'Password attempts exceeded') {
        requestFailed(
          'アカウントがロックされています。時間を置いて再度お試し下さい。',
        )
        return
      }
      const message = onlyPassword
        ? 'パスワードが誤っています'
        : 'メールアドレスまたは、パスワードが誤っています'
      requestFailed(message)
    } finally {
      requestDone()
    }
  }

  return {
    errorMessage,
    requesting,
    onSubmit,
  }
}
